import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { 
  Shield, Users, Bell, Download, Trash2, Check, X, RefreshCw, AlertTriangle, 
  Printer, MessageSquare, Bug, Lightbulb, HelpCircle, MessageCircle,
  ChevronDown, ChevronUp, Eye, CheckCircle, Send, Reply, User,
  Thermometer, Layers, Box, ArrowLeft, LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  bug: Bug,
  suggestion: Lightbulb,
  question: HelpCircle,
  general: MessageCircle,
};

const CATEGORY_COLORS = {
  bug: 'text-red-500 bg-red-500/10',
  suggestion: 'text-yellow-500 bg-yellow-500/10',
  question: 'text-blue-500 bg-blue-500/10',
  general: 'text-green-500 bg-green-500/10',
};

// Admin emails - same as main admin
const ADMIN_EMAILS = ['andre@cnc-helpdesk.com', 'booysena@gmail.com'];

export default function Printing3DAdmin() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [testersWithFeedback, setTestersWithFeedback] = useState([]);
  const [featureNotifications, setFeatureNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, email: null });
  const [refreshing, setRefreshing] = useState(false);
  const [expandedTesters, setExpandedTesters] = useState({});
  const [respondDialog, setRespondDialog] = useState({ open: false, feedback: null });
  const [respondText, setRespondText] = useState('');
  const [markResolved, setMarkResolved] = useState(true);
  const [responding, setResponding] = useState(false);

  // Handle logout
  const handleLogout = () => {
    if (logout) {
      logout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    navigate('/');
  };

  useEffect(() => {
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchAllData();
    }
  }, [isAdmin]);

  const checkAdminStatus = () => {
    if (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch stats, testers, and notifications filtered for 3D printing
      const [statsRes, testersRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?discipline=3d_printing`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/testers-with-feedback?discipline=3d_printing`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/feature-notifications`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setTestersWithFeedback(testersRes.data || []);
      // Filter notifications for 3D printing
      setFeatureNotifications((notificationsRes.data || []).filter(n => n.discipline === '3d_printing'));
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleApproveTester = async (applicationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use unified approval endpoint
      await axios.post(`${API}/admin/tester-applications/${applicationId}/approve`, {}, { headers });
      toast.success('Tester approved successfully');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve tester');
    }
  };

  const handleDeleteTester = async () => {
    if (!deleteDialog.id) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use unified delete endpoint
      await axios.delete(`${API}/admin/tester-applications/${deleteDialog.id}`, { headers });
      toast.success('Tester application deleted');
      setDeleteDialog({ open: false, type: null, id: null, email: null });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tester');
    }
  };

  const handleRespondToFeedback = async () => {
    if (!respondDialog.feedback || !respondText.trim()) {
      toast.error('Please enter a response');
      return;
    }
    
    setResponding(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use unified feedback respond endpoint
      await axios.post(`${API}/admin/beta-feedback/${respondDialog.feedback.id}/respond`, {
        response: respondText,
        mark_resolved: markResolved
      }, { headers });
      
      toast.success('Response sent successfully');
      setRespondDialog({ open: false, feedback: null });
      setRespondText('');
      setMarkResolved(true);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  const toggleTesterExpanded = (testerId) => {
    setExpandedTesters(prev => ({
      ...prev,
      [testerId]: !prev[testerId]
    }));
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Card className="bg-zinc-900 border-zinc-800 max-w-md">
          <CardHeader>
            <CardTitle className="text-red-500 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Access Denied
            </CardTitle>
            <CardDescription className="text-zinc-400">
              You don't have permission to access the 3D Printing Admin Panel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/3d-printing')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading 3D Printing Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] p-6" data-testid="printing3d-admin">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link 
            to="/admin" 
            className="flex items-center gap-2 px-3 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </Link>
          <div className="w-px h-8 bg-zinc-700" />
          <div className="p-2 bg-orange-500/20 rounded-lg">
            <Shield className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">3D Printing Admin Panel</h1>
            <p className="text-zinc-400">Manage testers, feedback, and notifications for 3D Printing Edition</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            className="border-zinc-700"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            data-testid="printing3d-admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Total Testers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats?.total_testers || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Pending Applications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-500">{stats?.pending_testers || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Active Testers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-500">{stats?.approved_testers || 0}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-400">Feedback Items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-500">{stats?.total_feedback || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-800 mb-6">
          <TabsTrigger value="overview" className="data-[state=active]:bg-orange-600">
            Overview
          </TabsTrigger>
          <TabsTrigger value="testers" className="data-[state=active]:bg-orange-600">
            Testers & Feedback
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-orange-600">
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activity */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Printer className="w-5 h-5 text-orange-500" />
                  3D Printing Edition Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <h4 className="text-white font-medium mb-2">Module Status</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-zinc-300">Print Calculator</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-zinc-300">Troubleshooting Guide</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-zinc-300">Materials Guide</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-zinc-300">Printer Setup</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <h4 className="text-white font-medium mb-2">Quick Stats</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Preset Printers Available</span>
                      <span className="text-white">20</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Materials Documented</span>
                      <span className="text-white">6</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Troubleshooting Issues</span>
                      <span className="text-white">12</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pending Actions */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Pending Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.pending_testers > 0 && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-yellow-400">
                        {stats.pending_testers} pending tester application(s)
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-yellow-500 text-yellow-500"
                        onClick={() => setActiveTab('testers')}
                      >
                        Review
                      </Button>
                    </div>
                  </div>
                )}
                
                {stats?.unresolved_feedback > 0 && (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-400">
                        {stats.unresolved_feedback} unresolved feedback item(s)
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="border-blue-500 text-blue-500"
                        onClick={() => setActiveTab('testers')}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                )}

                {(!stats?.pending_testers && !stats?.unresolved_feedback) && (
                  <div className="text-center py-8 text-zinc-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                    <p>All caught up! No pending actions.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Testers & Feedback Tab */}
        <TabsContent value="testers">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" />
                3D Printing Testers & Feedback
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Manage tester applications and feedback for 3D Printing Edition
              </CardDescription>
            </CardHeader>
            <CardContent>
              {testersWithFeedback.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-3" />
                  <p>No 3D printing testers yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {testersWithFeedback.map((tester) => (
                    <Collapsible 
                      key={tester.id} 
                      open={expandedTesters[tester.id]}
                      onOpenChange={() => toggleTesterExpanded(tester.id)}
                    >
                      <div className="border border-zinc-800 rounded-lg overflow-hidden">
                        <CollapsibleTrigger className="w-full">
                          <div className="p-4 bg-zinc-800/60 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-orange-500" />
                              </div>
                              <div className="text-left">
                                <div className="text-white font-medium">
                                  {tester.first_name} {tester.surname}
                                </div>
                                <div className="text-sm text-zinc-400">{tester.email}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={
                                tester.status === 'approved' ? 'bg-green-600' :
                                tester.status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'
                              }>
                                {tester.status}
                              </Badge>
                              {expandedTesters[tester.id] ? (
                                <ChevronUp className="w-5 h-5 text-zinc-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-zinc-400" />
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="p-4 border-t border-zinc-800 space-y-4">
                            {/* Tester Details */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-zinc-500">Country:</span>
                                <span className="text-white ml-2">{tester.country}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Experience:</span>
                                <span className="text-white ml-2">{tester.experience_level}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Printer:</span>
                                <span className="text-white ml-2">{tester.printer_brand || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Applied:</span>
                                <span className="text-white ml-2">
                                  {new Date(tester.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2">
                              {tester.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-500"
                                  onClick={() => handleApproveTester(tester.id)}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="border-red-500 text-red-500 hover:bg-red-500/10"
                                onClick={() => setDeleteDialog({ 
                                  open: true, 
                                  type: 'tester', 
                                  id: tester.id, 
                                  email: tester.email 
                                })}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>

                            {/* Feedback from this tester */}
                            {tester.feedback && tester.feedback.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-zinc-800">
                                <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                                  <MessageSquare className="w-4 h-4 text-blue-500" />
                                  Feedback ({tester.feedback.length})
                                </h4>
                                <div className="space-y-3">
                                  {tester.feedback.map((fb) => {
                                    const CategoryIcon = CATEGORY_ICONS[fb.category] || MessageCircle;
                                    return (
                                      <div key={fb.id} className="p-3 bg-zinc-800/50 rounded-lg">
                                        <div className="flex items-start justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <CategoryIcon className={`w-4 h-4 ${CATEGORY_COLORS[fb.category]?.split(' ')[0]}`} />
                                            <span className="text-white font-medium">{fb.subject}</span>
                                          </div>
                                          <Badge variant="outline" className={
                                            fb.status === 'resolved' ? 'border-green-500 text-green-500' :
                                            fb.status === 'reviewed' ? 'border-blue-500 text-blue-500' :
                                            'border-zinc-500 text-zinc-500'
                                          }>
                                            {fb.status}
                                          </Badge>
                                        </div>
                                        <p className="text-sm text-zinc-400 mb-2">{fb.message}</p>
                                        {fb.admin_response && (
                                          <div className="p-2 bg-green-500/10 border border-green-500/30 rounded mt-2">
                                            <div className="text-xs text-green-400 mb-1">Admin Response:</div>
                                            <p className="text-sm text-green-300">{fb.admin_response}</p>
                                          </div>
                                        )}
                                        {fb.status !== 'resolved' && (
                                          <Button 
                                            size="sm" 
                                            variant="outline" 
                                            className="mt-2 border-blue-500 text-blue-500"
                                            onClick={() => {
                                              setRespondDialog({ open: true, feedback: fb });
                                              setRespondText('');
                                            }}
                                          >
                                            <Reply className="w-3 h-3 mr-1" />
                                            Respond
                                          </Button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-orange-500" />
                3D Printing Notification Requests
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Users who requested to be notified about 3D Printing Edition
              </CardDescription>
            </CardHeader>
            <CardContent>
              {featureNotifications.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Bell className="w-12 h-12 mx-auto mb-3" />
                  <p>No notification requests</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400">Email</TableHead>
                      <TableHead className="text-zinc-400">Date</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {featureNotifications.map((notification) => (
                      <TableRow key={notification.id} className="border-zinc-800">
                        <TableCell className="text-white">{notification.email}</TableCell>
                        <TableCell className="text-zinc-400">
                          {new Date(notification.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge className={notification.notified ? 'bg-green-600' : 'bg-yellow-600'}>
                            {notification.notified ? 'Notified' : 'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete {deleteDialog.email}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-500" onClick={handleDeleteTester}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Respond to Feedback Dialog */}
      <Dialog open={respondDialog.open} onOpenChange={(open) => setRespondDialog({ ...respondDialog, open })}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">Respond to Feedback</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {respondDialog.feedback?.subject}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Original Message</Label>
              <p className="text-sm text-zinc-300 mt-1 p-3 bg-zinc-800 rounded">
                {respondDialog.feedback?.message}
              </p>
            </div>
            <div>
              <Label className="text-zinc-400">Your Response</Label>
              <Textarea
                value={respondText}
                onChange={(e) => setRespondText(e.target.value)}
                placeholder="Type your response..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                rows={4}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="markResolved" 
                checked={markResolved} 
                onCheckedChange={setMarkResolved}
              />
              <Label htmlFor="markResolved" className="text-zinc-400">
                Mark as resolved after sending
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondDialog({ open: false, feedback: null })}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-500"
              onClick={handleRespondToFeedback}
              disabled={responding}
            >
              {responding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
